import { useEffect, useMemo } from 'react';
import {
  SportsEnum,
  type VenuesBoolExp,
  useGetBookableCourtsByGeoLazyQuery,
} from 'types/generated/client';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import type { FindFilters } from 'screens/Find/filters';
import { searchCenterFor, toGeographyPoint } from 'screens/Find/geo';
import { useViewerCenter } from 'screens/Find/useViewerCenter';
import { HomeFilters } from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import { chosenRadiusMiles } from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/sheetFilters';
import { FEED_RADIUS_MILES } from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/useTopOpportunities';

/** Enough for the featured card plus a full desktop row underneath. */
const FEED_VENUE_COUNT = 9;

const venueSportFilter = (sport: SportsEnum): VenuesBoolExp => {
  if (sport === SportsEnum.Tennis) return { hasTennis: { _eq: true } };
  if (sport === SportsEnum.Padel) return { hasPadel: { _eq: true } };
  return { hasPickleball: { _eq: true } };
};

// Reuses the Courts section's query with a longer limit; the partner filter rides in
// with the sport filter so nine ordinary clubs can't crowd out a live partner.
export const useBookableFeedVenues = (filters: HomeFilters, sheetFilters: FindFilters) => {
  const [getBookableCourts, { data, loading }] = useGetBookableCourtsByGeoLazyQuery();

  const radiusMiles = chosenRadiusMiles(sheetFilters) || FEED_RADIUS_MILES;
  const viewerCenter = useViewerCenter();
  const searchCenter = useMemo(
    () => searchCenterFor(sheetFilters.neighborhoods, viewerCenter),
    [sheetFilters.neighborhoods, viewerCenter],
  );

  useEffect(() => {
    getBookableCourts({
      variables: {
        limit: FEED_VENUE_COUNT,
        distance: milesToMeters(radiusMiles),
        from: toGeographyPoint(searchCenter),
        sportFilter: {
          _and: [
            venueSportFilter(filters.sport),
            { partnerBookingStatus: { isBookable: { _eq: true } } },
          ],
        },
      },
    });
  }, [searchCenter, filters.sport, radiusMiles, getBookableCourts]);

  return { venues: data?.venues ?? [], loading };
};
